import { useEffect, useState, useCallback } from 'react';
import { checkForStaleChunks, wasStaleChunkDetected, clearStaleChunkFlag } from '@/lib/modulePreloader';
import { invalidateAllCaches, initializeCacheManager } from '@/lib/serviceWorkerManager';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Periodically checks for app version updates and prompts user to refresh
 * when stale chunks are detected. Also manages service worker cache invalidation.
 */
export function VersionChecker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const handleRefresh = useCallback(async () => {
    // Clear all caches before reloading
    await invalidateAllCaches();
    window.location.reload();
  }, []);

  const showUpdateToast = useCallback(() => {
    if (updateAvailable) return; // Prevent duplicate toasts
    
    setUpdateAvailable(true);
    toast.info('A new version is available', {
      description: 'Refresh to get the latest updates',
      duration: Infinity,
      action: {
        label: 'Refresh',
        onClick: handleRefresh,
      },
    });
  }, [updateAvailable, handleRefresh]);

  useEffect(() => {
    // Initialize cache manager on mount
    initializeCacheManager();

    // Check immediately if stale chunks were detected during lazy loading
    if (wasStaleChunkDetected()) {
      clearStaleChunkFlag();
      showUpdateToast();
    }

    // Check periodically
    const interval = setInterval(async () => {
      const isStale = await checkForStaleChunks();
      if (isStale) {
        showUpdateToast();
      }
    }, CHECK_INTERVAL_MS);

    // Check on tab visibility change (user returns to tab)
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        // Also check for stale chunk flag
        if (wasStaleChunkDetected()) {
          clearStaleChunkFlag();
          showUpdateToast();
          return;
        }
        
        const isStale = await checkForStaleChunks();
        if (isStale) {
          showUpdateToast();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [showUpdateToast]);

  // This component doesn't render anything visible directly
  // It uses toast for notifications
  return null;
}
